import React from "react";

// The LetsLoop brand mesh gradient, as a full page surface.
//
// This is the app's signature look (coral / gold / peach soft radial blobs over
// cream, centre kept light so type stays readable) brought to the website for
// the first time. The native app has MeshGradientBackdrop; the site had no
// equivalent at all, so this is the missing half of the brand.
//
// Built from layered CSS radial-gradients rather than a canvas ON PURPOSE: this
// site is SSR-prerendered, so a canvas would paint nothing until hydration and
// the hero would flash cream on first paint. Gradients prerender, cost no JS,
// and cause no layout shift. The scroll-driven canvas belongs in MeshMoment,
// where the motion IS the content.
//
// Every colour resolves through the design tokens, so the surface flips with
// the light / dark theme instead of being pinned to a literal hex.

type Props = {
  /** `hero` is warmer and denser; `ambient` sits behind content without competing. */
  variant?: "hero" | "ambient";
  /** Drift the blobs slowly. Ignored under prefers-reduced-motion. */
  animated?: boolean;
  className?: string;
};

const MeshField: React.FC<Props> = ({
  variant = "hero",
  animated = true,
  className = "",
}) => {
  const hero = variant === "hero";

  return (
    <div
      aria-hidden="true"
      className={`mesh-field ${hero ? "mesh-hero" : "mesh-ambient"} ${
        animated ? "mesh-animated" : ""
      } ${className}`}
    >
      <style>{`
        .mesh-field {
          position: absolute;
          inset: 0;
          overflow: hidden;
          pointer-events: none;
          background: var(--color-cream);
        }
        /* Each blob is its own layer so they can drift independently.
           Opacity is carried in the colour stops, not on the element, so the
           blobs blend with each other instead of stacking into a flat wash. */
        .mesh-blob {
          position: absolute;
          border-radius: 9999px;
          filter: blur(64px);
          will-change: transform;
        }
        .mesh-hero .mesh-blob { filter: blur(72px); }
        .mesh-ambient .mesh-blob { filter: blur(88px); opacity: 0.55; }

        /* Centre stays light: the hero headline sits here and must stay legible
           against the surface at AA. The blobs are pushed to the edges.
           The veil is deliberately TIGHTER than a first pass would suggest
           (52%/40% rather than 60%/55%): a wider veil washed the colour out at
           desktop width and the surface read as a pale gradient rather than the
           brand's signature mesh. It now clears only the text column. */
        .mesh-topblend {
          position: absolute;
          inset: 0 0 auto 0;
          height: 56px;
          background: linear-gradient(
            to bottom,
            var(--mesh-strip, #f9d0b4) 0%,
            color-mix(in srgb, var(--mesh-strip, #f9d0b4) 55%, transparent) 45%,
            transparent 100%
          );
        }

        .mesh-veil {
          position: absolute;
          inset: 0;
          background: radial-gradient(
            52% 40% at 50% 46%,
            color-mix(in srgb, var(--color-cream) 86%, transparent) 0%,
            color-mix(in srgb, var(--color-cream) 34%, transparent) 62%,
            transparent 100%
          );
        }

        @media (prefers-reduced-motion: no-preference) {
          .mesh-animated .mesh-blob { animation: mesh-drift 26s ease-in-out infinite alternate; }
          .mesh-animated .mesh-blob:nth-child(2) { animation-duration: 34s; animation-delay: -6s; }
          .mesh-animated .mesh-blob:nth-child(3) { animation-duration: 30s; animation-delay: -13s; }
          .mesh-animated .mesh-blob:nth-child(4) { animation-duration: 38s; animation-delay: -19s; }
        }
        @keyframes mesh-drift {
          from { transform: translate3d(0, 0, 0) scale(1); }
          to   { transform: translate3d(2.5%, -3%, 0) scale(1.09); }
        }
      `}</style>

      {/* Coral, top left: the brand accent, given the most weight. */}
      <div
        className="mesh-blob"
        style={{
          top: hero ? "-18%" : "-24%",
          left: "-12%",
          width: "62%",
          height: "68%",
          background:
            "radial-gradient(circle at 50% 50%, var(--color-salmon) 0%, color-mix(in srgb, var(--color-salmon) 72%, transparent) 42%, transparent 74%)",
        }}
      />
      {/* Gold, top right. */}
      <div
        className="mesh-blob"
        style={{
          top: "-22%",
          right: "-14%",
          width: "58%",
          height: "62%",
          background:
            "radial-gradient(circle at 50% 50%, #F5BE6E 0%, rgba(245,190,110,0.68) 44%, transparent 76%)",
        }}
      />
      {/* Peach, lower right, anchors the fold. */}
      <div
        className="mesh-blob"
        style={{
          bottom: "-26%",
          right: "-6%",
          width: "66%",
          height: "64%",
          background:
            "radial-gradient(circle at 50% 50%, var(--color-salmon-light) 0%, color-mix(in srgb, var(--color-salmon) 58%, transparent) 46%, transparent 78%)",
        }}
      />
      {/* A single cool note so the surface is a mesh and not a sunset. Teal is
          the in-app accent, kept to a whisper here. */}
      <div
        className="mesh-blob"
        style={{
          bottom: "-30%",
          left: "6%",
          width: "48%",
          height: "52%",
          background:
            "radial-gradient(circle at 50% 50%, color-mix(in srgb, var(--color-teal) 34%, transparent) 0%, transparent 72%)",
        }}
      />

      {/* Top blend.
          iOS Safari paints the strip behind the status bar with a single
          RESOLVED COLOUR: it does not render a background image there. Proven
          rather than assumed, from a founder screenshot in which that strip was
          uniformly #f9d0b4, our body background-COLOR, while the mesh rendered
          correctly a pixel below it. So no gradient can ever be shown up there,
          and every attempt to put one there was chasing something the platform
          does not do.
          The seam is therefore closed from below instead: the first 56px of the
          hero fade out of exactly that flat colour and into the mesh, so the
          strip and the page meet with no visible edge. */}
      <div className="mesh-topblend" />

      <div className="mesh-veil" />
    </div>
  );
};

export default MeshField;
